import type React from 'react';
import { useContext, Suspense } from 'react';
import { ModalContext } from '../ModalProvider';
import PopupModal from './PopupModal';
import ModalErrorBoundary from './ModalErrorBoundary';
import { getModalComponent } from '../registry';

/**
 * ModalPortal renders all active modals from the ModalProvider context.
 * It maps through activeModals and renders each one using the PopupModal component.
 * Modals are automatically sorted by priority (highest first) in the ModalProvider.
 */
const ModalPortal: React.FC = () => {
  const { activeModals, closeModal } = useContext(ModalContext);

  // If no modals are active, render nothing
  if (activeModals.length === 0) {
    return null;
  }

  return (
    <>
      {activeModals.map((modalConfig, index) => {
        const handleClose = () => {
          closeModal(modalConfig.id);
        };

        // Only the first modal (highest priority) should handle ESC key
        const shouldHandleEscKey = index === 0;

        // Only the first modal (highest priority) should receive initial focus
        const shouldReceiveInitialFocus = index === 0;

        // Load the actual modal component from the registry
        // Type assertion is safe here as modalConfig.type comes from openModal calls
        const ModalComponent = getModalComponent(modalConfig.type as any);

        // Extract PopupModal-specific props from modalConfig.props
        const {
          hideHeader,
          hideCloseButton,
          hideFooter,
          useCloseButton,
          customCloseIcon,
          darkOverlay,
          transparentOverlay,
          floatingCloseButton,
          fullHeight,
          isFullScreen,
          isWebview,
          paddingBottom,
          paddingInline,
          paddingTop,
          position,
          scrollContainerOverride,
          scrollablePaddingInline,
          size,
          slideUp,
          ...componentProps
        } = modalConfig.props || {};

        // If no component is registered, show placeholder
        const modalContent = ModalComponent ? (
          <Suspense fallback={null}>
            <ModalComponent {...componentProps} onClose={handleClose} />
          </Suspense>
        ) : (
          <div>
            <h2>Modal: {modalConfig.type}</h2>
            <p>Modal ID: {modalConfig.id}</p>
            <p>Priority: {modalConfig.priority}</p>
            <p>This modal type is not registered in the modal registry.</p>
          </div>
        );

        return (
          <ModalErrorBoundary key={modalConfig.id} modalType={modalConfig.type}>
            <PopupModal
              modalConfig={modalConfig}
              onClose={handleClose}
              // Pass extracted PopupModal-specific props
              hideHeader={hideHeader}
              hideCloseButton={hideCloseButton}
              hideFooter={hideFooter}
              useCloseButton={useCloseButton}
              customCloseIcon={customCloseIcon}
              darkOverlay={darkOverlay}
              transparentOverlay={transparentOverlay}
              floatingCloseButton={floatingCloseButton}
              fullHeight={fullHeight}
              isFullScreen={isFullScreen}
              isWebview={isWebview}
              paddingBottom={paddingBottom}
              paddingInline={paddingInline}
              paddingTop={paddingTop}
              position={position}
              scrollContainerOverride={scrollContainerOverride}
              scrollablePaddingInline={scrollablePaddingInline}
              size={size}
              slideUp={slideUp}
              // Use modal type as aria-label for accessibility
              ariaLabel={`${modalConfig.type} modal`}
              ariaLabelledBy={`${modalConfig.type}-modal-title`}
              // Only highest priority modal handles ESC and gets focus
              shouldHandleEscKey={shouldHandleEscKey}
              shouldReceiveInitialFocus={shouldReceiveInitialFocus}
            >
              {modalContent}
            </PopupModal>
          </ModalErrorBoundary>
        );
      })}
    </>
  );
};

export default ModalPortal;
